import { Fragment, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Button, useToast } from '../../components/primitives';
import { SendEmailModal } from '../../components/SendEmailModal';
import { buildDocHtml } from '../../lib/docHtml';
import { printToPdf } from '../../lib/printToPdf';
import {
  DocLayout,
  DocPage,
  DocBlock,
  DocRow,
  DocSignatures,
  DocContactQR,
  DocInlineDate,
  DocInlineAresName,
  DocInlineAresLookup,
  DocActionButton,
  DocDphSelect,
  SavedDocsButton,
  NewDocButton,
  DraftBanner,
  useDocChain,
  useDocTransferIn,
  DocChainBar,
  DocTransferButton,
  useDocRows,
  DocCustomRows,
  DocAddRowButton,
} from '../../components/document';
import { MapPickerButton } from '../../components/map';
import {
  CompanyPickerButton,
  SaveCompanyButton,
  type AresPickResult,
  type CompanyPickResult,
} from '../../components/ares';
import {
  StavbaPickerButton,
  SaveStavbaButton,
  type StavbaPickResult,
} from '../../components/stavba';
import { useSaveDocument } from '../../api/hooks';
import { useDocEditor } from '../../lib/useDocEditor';
import { useDocDraft } from '../../lib/useDocDraft';
import { docSaveErrorMessage } from '../../lib/docSaveError';
import { useActiveStavba } from '../stavba-context/StavbaContext';

/**
 * Soupis prací — "živý dokument".
 * Sekce: Údaje soupisu / Stavba / Zhotovitel / Objednatel /
 * Položky prací a dodávek (inline tabulka) / Podpisy.
 */

type RowType = 'section' | 'item';
interface SpRow {
  id: string;
  type: RowType;
  /** Pro section: titulek. Pro item: popis. */
  title: string;
  unit?: string;
  qty?: string;
  price?: string;
}

interface ExtraSig {
  id: string;
  strana: string;
  jmeno: string;
  funkce: string;
  image: string;
}

function newId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

function parseCz(s: string | undefined): number {
  if (!s) return 0;
  const cleaned = s
    .replace(/\s/g, '')
    .replace(/Kč/gi, '')
    .replace(',', '.')
    .replace(/[^\d.-]/g, '');
  const n = parseFloat(cleaned);
  return isFinite(n) ? n : 0;
}

function fmtCz(n: number): string {
  return (
    n.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' Kč'
  );
}

function deriveInitials(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .filter(
      (w) =>
        w &&
        /[\p{L}]/u.test(w[0]) &&
        !/^(s\.?r\.?o\.?|a\.?s\.?|spol|ltd|inc)$/i.test(w),
    );
  if (words.length === 0) return '—';
  return words
    .slice(0, 3)
    .map((w) => w[0].toLocaleUpperCase('cs'))
    .join('');
}

export function SoupisPage() {
  const saveDoc = useSaveDocument();
  const toast = useToast();
  const { activeStavba } = useActiveStavba();
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [emailOpen, setEmailOpen] = useState(false);

  const [cislo, setCislo] = useState('');
  const [datum, setDatum] = useState('');
  const [zakazka, setZakazka] = useState('');
  const [zpCislo, setZpCislo] = useState('');

  const [stavba, setStavba] = useState({
    nazev: '',
    iniciály: '',
    adresa: '',
    cPovoleni: '',
    sodCislo: '',
    sodDatum: '',
  });

  const [zhot, setZhot] = useState({
    nazev: '',
    sidlo: '',
    ico: '',
    dic: '',
    tel: '',
    email: '',
  });

  const [obj, setObj] = useState({
    nazev: '',
    sidlo: '',
    ico: '',
    dic: '',
    tel: '',
    email: '',
  });

  const [rows, setRows] = useState<SpRow[]>([
    { id: newId('r'), type: 'item', title: '', unit: '', qty: '', price: '' },
  ]);

  const [dphPct, setDphPct] = useState('21');

  const [podpisy, setPodpisy] = useState({
    zhotovitel: { jmeno: '', funkce: '', image: '' },
    objednatel: { jmeno: '', funkce: '', image: '' },
    extra: [] as ExtraSig[],
  });

  // Skryté řádky — uživatel může jednotlivé řádky odebrat ✕,
  // skupinové „obnovit" je pak v hlavičce sekce. Plus vlastní řádky v sekcích.
  // (`rows` jsou položky soupisu, proto `docRows`.)
  const chain = useDocChain();
  const docRows = useDocRows();
  const { isHidden, hide, restoreSection, hiddenCount } = docRows;

  const calc = useMemo(() => {
    let total = 0;
    const rowTotals = new Map<string, number>();
    let itemIdx = 0;
    const numbered: Array<{ row: SpRow; n: number; total: number }> = [];
    for (const r of rows) {
      if (r.type === 'item') {
        itemIdx++;
        const rt = parseCz(r.qty) * parseCz(r.price);
        total += rt;
        rowTotals.set(r.id, rt);
        numbered.push({ row: r, n: itemIdx, total: rt });
      } else {
        numbered.push({ row: r, n: 0, total: 0 });
      }
    }
    const rate = (parseFloat(dphPct) || 0) / 100;
    const dph = total * rate;
    return { total, dph, rowTotals, numbered, grandTotal: total + dph };
  }, [rows, dphPct]);

  function updateRow(id: string, patch: Partial<SpRow>) {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }
  function removeRow(id: string) {
    setRows((rs) => rs.filter((r) => r.id !== id));
  }
  function addItem() {
    setRows((rs) => [
      ...rs,
      { id: newId('r'), type: 'item', title: '', unit: 'm²', qty: '', price: '' },
    ]);
  }
  function addSection() {
    setRows((rs) => [...rs, { id: newId('r'), type: 'section', title: 'Nová sekce' }]);
  }
  function moveRow(id: string, dir: -1 | 1) {
    setRows((rs) => {
      const i = rs.findIndex((r) => r.id === id);
      if (i < 0) return rs;
      const j = i + dir;
      if (j < 0 || j >= rs.length) return rs;
      const copy = [...rs];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  }

  const addExtra = () =>
    setPodpisy((p) => ({
      ...p,
      extra: [
        ...p.extra,
        { id: newId('ex'), strana: 'Účastník', jmeno: '', funkce: '', image: '' },
      ],
    }));
  const updateExtra = (id: string, patch: Partial<ExtraSig>) =>
    setPodpisy((p) => ({
      ...p,
      extra: p.extra.map((x) => (x.id === id ? { ...x, ...patch } : x)),
    }));
  const removeExtra = (id: string) =>
    setPodpisy((p) => ({ ...p, extra: p.extra.filter((x) => x.id !== id) }));

  const setObjPatch = (patch: Partial<typeof obj>) => setObj((o) => ({ ...o, ...patch }));
  const setZhotPatch = (patch: Partial<typeof zhot>) => setZhot((z) => ({ ...z, ...patch }));
  const setStavbaPatch = (patch: Partial<typeof stavba>) =>
    setStavba((s) => ({ ...s, ...patch }));

  const docTitle = cislo ? `Soupis prací ${cislo}` : 'Soupis prací';
  const metaText = [stavba.nazev, datum].filter(Boolean).join(' · ');

  /** Předvyplní formulář ze snímku dat (dokument z archivu i lokální koncept). */
  function applySnapshot(d: Record<string, unknown>) {
    if (d.cislo != null) setCislo(String(d.cislo));
    if (d.datum != null) setDatum(String(d.datum));
    if (d.zakazka != null) setZakazka(String(d.zakazka));
    if (d.zpCislo != null) setZpCislo(String(d.zpCislo));
    if (d.stavba) setStavba((s) => ({ ...s, ...(d.stavba as Partial<typeof stavba>) }));
    if (d.zhot) setZhot((x) => ({ ...x, ...(d.zhot as Partial<typeof zhot>) }));
    if (d.obj) setObj((x) => ({ ...x, ...(d.obj as Partial<typeof obj>) }));
    if (Array.isArray(d.rows)) setRows(d.rows as SpRow[]);
    if (d.dphPct != null) setDphPct(String(d.dphPct));
    if (d.podpisy) setPodpisy((p) => ({ ...p, ...(d.podpisy as Partial<typeof podpisy>) }));
    chain.apply(d);
    docRows.apply(d.radky);
  }

  const docData = {
    cislo, datum, zakazka, zpCislo, stavba, zhot, obj, rows, dphPct, podpisy,
    radky: docRows.snapshot(),
    zdroj: chain.zdroj,
  };
  const { editId } = useDocEditor(applySnapshot);
  useDocTransferIn('soupis', applySnapshot, editId);

  // Autosave rozpracovaného konceptu do prohlížeče (jen u nového dokumentu) —
  // bez něj se při zavření/reloadu záložky ztratil celý rozepsaný formulář.
  const draft = useDocDraft<Record<string, unknown>>('soupis', editId == null);
  function restoreDraft() {
    if (!draft.pending) return;
    applySnapshot(draft.pending.data);
    draft.accept();
  }
  useEffect(() => {
    draft.persist(docData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cislo, datum, zakazka, zpCislo, stavba, zhot, obj, rows, dphPct, podpisy, docRows, chain.zdroj]);

  /**
   * Uložení do archivu. Chybu je nutné ukázat — dřív se `mutateAsync` odmítlo
   * bez ošetření a uživatel si myslel, že je dokument uložený.
   */
  async function handleSave(): Promise<number | null> {
    try {
      const res = await saveDoc.mutateAsync({
        id: editId ?? chain.savedId ?? undefined,
        doc_type: 'soupis',
        title: docTitle,
        stavba: activeStavba?.nazev ?? '',
        data_json: docData,
      });
      draft.clear();
      toast.success('Soupis prací uložen do archivu.');
      chain.markSaved(res.id);
      return res.id;
    } catch (e) {
      toast.error(docSaveErrorMessage(e));
      return null;
    }
  }

  return (
    <>
      <DocLayout
        breadcrumb={['Dokumenty', 'Soupis prací']}
        title={docTitle}
        statusText="Rozpracovaný koncept"
        status="draft"
        actions={
          <>
            <NewDocButton draftKey="soupis" path="/app/soupis" />
            <SavedDocsButton
              docType="soupis"
              title="Uložené soupisy prací"
              currentId={editId}
            />
            <Button variant="secondary" leading="⎙" onClick={() => printToPdf(`Soupis_praci_${cislo || 'koncept'}.pdf`)}>PDF</Button>
            <Button variant="secondary" leading="📧" onClick={() => setEmailOpen(true)}>
              Odeslat e-mailem
            </Button>
            <DocTransferButton
              docType="soupis"
              getData={() => docData}
              save={handleSave}
              title={docTitle}
              zdroj={chain.zdroj}
            />
            <Button variant="primary" onClick={handleSave} disabled={saveDoc.isPending}>
              {saveDoc.isPending ? 'Ukládám…' : saveDoc.isSuccess ? 'Uloženo ✓' : 'Uložit'}
            </Button>
          </>
        }
      >
        <DocChainBar zdroj={chain.zdroj} />
        {draft.pending && (
          <DraftBanner
            savedAt={draft.pending.savedAt}
            onRestore={restoreDraft}
            onDiscard={draft.clear}
          />
        )}
        <div ref={previewRef}>
          <DocPage
            company={{
              initials: stavba.iniciály || deriveInitials(stavba.nazev || zhot.nazev),
              name: zhot.nazev || '—',
              address: `${zhot.sidlo || '—'}${zhot.ico ? ' · IČ ' + zhot.ico : ''}`,
            }}
            tag="Soupis prací"
            title={
              <>
                Soupis prací a dodávek
                {cislo && (
                  <>
                    {' '}
                    <span style={{ fontWeight: 400, fontSize: 14, color: 'var(--sd-doc-dim)' }}>
                      · č. {cislo}
                    </span>
                  </>
                )}
              </>
            }
            meta={metaText || undefined}
            footer={{ left: zhot.nazev || '—', center: 'str. 1 / 1', right: zakazka || '—' }}
          >
            <DocBlock
              idx="01"
              title="Údaje soupisu"
              hiddenCount={hiddenCount('s01')}
              onRestoreHidden={() => restoreSection('s01')}
              actions={<DocAddRowButton rows={docRows} section="s01" />}
            >
              {!isHidden('s01.cislo') && (
                <DocRow
                  k="Číslo soupisu"
                  mono
                  value={cislo}
                  onChange={setCislo}
                  placeholder="např. SP-2026/04"
                  onRemove={() => hide('s01.cislo')}
                />
              )}
              {!isHidden('s01.datum') && (
                <DocRow
                  k="Datum"
                  mono
                  value={datum}
                  onChange={setDatum}
                  placeholder="dd. mm. rrrr"
                  actions={<DocInlineDate value={datum} onChange={setDatum} />}
                  onRemove={() => hide('s01.datum')}
                />
              )}
              {!isHidden('s01.zakazka') && (
                <DocRow
                  k="Číslo zakázky"
                  mono
                  value={zakazka}
                  onChange={setZakazka}
                  placeholder="např. Z-2026/0412"
                  onRemove={() => hide('s01.zakazka')}
                />
              )}
              {!isHidden('s01.zpCislo') && (
                <DocRow
                  k="ZP č."
                  mono
                  value={zpCislo}
                  onChange={setZpCislo}
                  placeholder="např. ZP-2026/04"
                  onRemove={() => hide('s01.zpCislo')}
                />
              )}
              {!isHidden('s01.sodCislo') && (
                <DocRow
                  k="SOD č."
                  mono
                  value={stavba.sodCislo}
                  onChange={(v) => setStavbaPatch({ sodCislo: v })}
                  placeholder="např. SOD-2026/04"
                  onRemove={() => hide('s01.sodCislo')}
                />
              )}
              {!isHidden('s01.sodDatum') && (
                <DocRow
                  k="SOD ze dne"
                  mono
                  value={stavba.sodDatum}
                  onChange={(v) => setStavbaPatch({ sodDatum: v })}
                  placeholder="dd. mm. rrrr"
                  actions={
                    <DocInlineDate
                      value={stavba.sodDatum}
                      onChange={(v) => setStavbaPatch({ sodDatum: v })}
                    />
                  }
                  onRemove={() => hide('s01.sodDatum')}
                />
              )}
              <DocCustomRows rows={docRows} section="s01" />
            </DocBlock>

            <DocBlock
              idx="02"
              title="Stavba"
              hiddenCount={hiddenCount('s02')}
              onRestoreHidden={() => restoreSection('s02')}
              actions={
                <>
                  <DocAddRowButton rows={docRows} section="s02" />
                  <StavbaPickerButton
                    title="Načíst stavbu ze seznamu"
                    onPick={(p: StavbaPickResult) =>
                      setStavba((s) => ({
                        ...s,
                        nazev: p.nazev,
                        iniciály: deriveInitials(p.nazev),
                        adresa: p.adresa,
                        cPovoleni: p.stavebni_povoleni,
                        sodDatum: p.datum_zahajeni,
                      }))
                    }
                  />
                  <SaveStavbaButton
                    title="Uložit stavbu"
                    build={() => ({
                      nazev: stavba.nazev,
                      adresa: stavba.adresa,
                      stavebni_povoleni: stavba.cPovoleni,
                      zakazka_cislo: zakazka,
                      datum_zahajeni: stavba.sodDatum,
                    })}
                  />
                </>
              }
            >
              {!isHidden('s02.nazev') && (
                <DocRow
                  k="Název stavby"
                  placeholder="např. RD Březník — etapa II"
                  value={stavba.nazev}
                  onChange={(v) => {
                    const auto = deriveInitials(stavba.nazev);
                    setStavba((s) => ({
                      ...s,
                      nazev: v,
                      iniciály: !s.iniciály || s.iniciály === auto ? deriveInitials(v) : s.iniciály,
                    }));
                  }}
                  onRemove={() => hide('s02.nazev')}
                />
              )}
              {!isHidden('s02.iniciaty') && (
                <DocRow
                  k="Iniciály"
                  mono
                  value={stavba.iniciály}
                  onChange={(v) => setStavbaPatch({ iniciály: v })}
                  placeholder={deriveInitials(stavba.nazev)}
                  onRemove={() => hide('s02.iniciaty')}
                />
              )}
              {!isHidden('s02.adresa') && (
                <DocRow
                  k="Adresa staveniště"
                  value={stavba.adresa}
                  onChange={(v) => setStavbaPatch({ adresa: v })}
                  placeholder="např. Březník 142, 675 74"
                  actions={
                    <MapPickerButton
                      value={stavba.adresa}
                      onApply={(v) => setStavbaPatch({ adresa: v })}
                      title="Adresa staveniště"
                      label="Mapa"
                    />
                  }
                  onRemove={() => hide('s02.adresa')}
                />
              )}
              {!isHidden('s02.povoleni') && (
                <DocRow
                  k="Č. stav. povolení"
                  value={stavba.cPovoleni}
                  onChange={(v) => setStavbaPatch({ cPovoleni: v })}
                  placeholder="např. MÚ-2026/0341"
                  onRemove={() => hide('s02.povoleni')}
                />
              )}
              <DocCustomRows rows={docRows} section="s02" />
            </DocBlock>

            <DocBlock
              idx="03"
              title="Zhotovitel"
              hiddenCount={hiddenCount('s03')}
              onRestoreHidden={() => restoreSection('s03')}
              actions={
                <>
                  <DocAddRowButton rows={docRows} section="s03" />
                  <CompanyPickerButton
                    preferRole="zhotovitel"
                    title="Načíst zhotovitele z adresáře"
                    onPick={(c: CompanyPickResult) =>
                      setZhot({
                        nazev: c.nazev,
                        sidlo: c.adresa,
                        ico: c.ico,
                        dic: c.dic,
                        tel: c.kontakt_tel,
                        email: c.kontakt_email,
                      })
                    }
                  />
                  <SaveCompanyButton
                    title="Uložit zhotovitele do adresáře"
                    build={() => ({
                      role: 'zhotovitel',
                      nazev: zhot.nazev,
                      ico: zhot.ico,
                      dic: zhot.dic,
                      adresa: zhot.sidlo,
                      kontakt_tel: zhot.tel,
                      kontakt_email: zhot.email,
                    })}
                  />
                </>
              }
            >
              {!isHidden('s03.nazev') && (
                <DocRow
                  k="Název"
                  v={
                    <DocInlineAresName
                      value={zhot.nazev}
                      onChange={(v) => setZhotPatch({ nazev: v })}
                      onPick={(d: AresPickResult) =>
                        setZhotPatch({ nazev: d.nazev, ico: d.ico, sidlo: d.adresa })
                      }
                    />
                  }
                  onRemove={() => hide('s03.nazev')}
                />
              )}
              {!isHidden('s03.sidlo') && (
                <DocRow
                  k="Sídlo"
                  value={zhot.sidlo}
                  onChange={(v) => setZhotPatch({ sidlo: v })}
                  placeholder="ulice č.p., PSČ město"
                  actions={
                    <MapPickerButton
                      value={zhot.sidlo}
                      onApply={(v) => setZhotPatch({ sidlo: v })}
                      title="Sídlo zhotovitele"
                      label="Mapa"
                    />
                  }
                  onRemove={() => hide('s03.sidlo')}
                />
              )}
              {!isHidden('s03.ico') && (
                <DocRow
                  k="IČ"
                  mono
                  value={zhot.ico}
                  onChange={(v) => setZhotPatch({ ico: v })}
                  placeholder="12345678"
                  actions={
                    <DocInlineAresLookup
                      ico={zhot.ico}
                      onResult={(d) => setZhotPatch({ nazev: d.nazev, ico: d.ico, sidlo: d.adresa })}
                    />
                  }
                  onRemove={() => hide('s03.ico')}
                />
              )}
              {!isHidden('s03.dic') && (
                <DocRow
                  k="DIČ"
                  mono
                  value={zhot.dic}
                  onChange={(v) => setZhotPatch({ dic: v })}
                  placeholder="CZ12345678"
                  onRemove={() => hide('s03.dic')}
                />
              )}
              {!isHidden('s03.tel') && (
                <DocRow
                  k="Telefon"
                  mono
                  value={zhot.tel}
                  onChange={(v) => setZhotPatch({ tel: v })}
                  placeholder="+420 …"
                  inputType="tel"
                  onRemove={() => hide('s03.tel')}
                />
              )}
              {!isHidden('s03.email') && (
                <DocRow
                  k="E-mail"
                  mono
                  value={zhot.email}
                  onChange={(v) => setZhotPatch({ email: v })}
                  placeholder="email@…"
                  inputType="email"
                  actions={
                    <DocContactQR
                      name={zhot.nazev}
                      role="Zhotovitel"
                      phone={zhot.tel}
                      email={zhot.email}
                      title="QR vizitka — zhotovitel"
                    />
                  }
                  onRemove={() => hide('s03.email')}
                />
              )}
              <DocCustomRows rows={docRows} section="s03" />
            </DocBlock>

            <DocBlock
              idx="04"
              title="Objednatel"
              hiddenCount={hiddenCount('s04')}
              onRestoreHidden={() => restoreSection('s04')}
              actions={
                <>
                  <DocAddRowButton rows={docRows} section="s04" />
                  <CompanyPickerButton
                    preferRole="objednatel"
                    title="Načíst objednatele z adresáře"
                    onPick={(c: CompanyPickResult) =>
                      setObj({
                        nazev: c.nazev,
                        sidlo: c.adresa,
                        ico: c.ico,
                        dic: c.dic,
                        tel: c.kontakt_tel,
                        email: c.kontakt_email,
                      })
                    }
                  />
                  <SaveCompanyButton
                    title="Uložit objednatele do adresáře"
                    build={() => ({
                      role: 'objednatel',
                      nazev: obj.nazev,
                      ico: obj.ico,
                      dic: obj.dic,
                      adresa: obj.sidlo,
                      kontakt_tel: obj.tel,
                      kontakt_email: obj.email,
                    })}
                  />
                </>
              }
            >
              {!isHidden('s04.nazev') && (
                <DocRow
                  k="Název"
                  v={
                    <DocInlineAresName
                      value={obj.nazev}
                      onChange={(v) => setObjPatch({ nazev: v })}
                      onPick={(d: AresPickResult) =>
                        setObjPatch({ nazev: d.nazev, ico: d.ico, sidlo: d.adresa })
                      }
                    />
                  }
                  onRemove={() => hide('s04.nazev')}
                />
              )}
              {!isHidden('s04.sidlo') && (
                <DocRow
                  k="Sídlo"
                  value={obj.sidlo}
                  onChange={(v) => setObjPatch({ sidlo: v })}
                  placeholder="ulice č.p., PSČ město"
                  actions={
                    <MapPickerButton
                      value={obj.sidlo}
                      onApply={(v) => setObjPatch({ sidlo: v })}
                      title="Sídlo objednatele"
                      label="Mapa"
                    />
                  }
                  onRemove={() => hide('s04.sidlo')}
                />
              )}
              {!isHidden('s04.ico') && (
                <DocRow
                  k="IČ"
                  mono
                  value={obj.ico}
                  onChange={(v) => setObjPatch({ ico: v })}
                  placeholder="12345678"
                  actions={
                    <DocInlineAresLookup
                      ico={obj.ico}
                      onResult={(d) => setObjPatch({ nazev: d.nazev, ico: d.ico, sidlo: d.adresa })}
                    />
                  }
                  onRemove={() => hide('s04.ico')}
                />
              )}
              {!isHidden('s04.dic') && (
                <DocRow
                  k="DIČ"
                  mono
                  value={obj.dic}
                  onChange={(v) => setObjPatch({ dic: v })}
                  placeholder="CZ12345678"
                  onRemove={() => hide('s04.dic')}
                />
              )}
              {!isHidden('s04.tel') && (
                <DocRow
                  k="Telefon"
                  mono
                  value={obj.tel}
                  onChange={(v) => setObjPatch({ tel: v })}
                  placeholder="+420 …"
                  inputType="tel"
                  onRemove={() => hide('s04.tel')}
                />
              )}
              {!isHidden('s04.email') && (
                <DocRow
                  k="E-mail"
                  mono
                  value={obj.email}
                  onChange={(v) => setObjPatch({ email: v })}
                  placeholder="email@…"
                  inputType="email"
                  actions={
                    <DocContactQR
                      name={obj.nazev}
                      role="Objednatel"
                      phone={obj.tel}
                      email={obj.email}
                      title="QR vizitka — objednatel"
                    />
                  }
                  onRemove={() => hide('s04.email')}
                />
              )}
              <DocCustomRows rows={docRows} section="s04" />
            </DocBlock>

            <DocBlock
              idx="05"
              title="Položky prací a dodávek"
              actions={
                <>
                  <DocActionButton onClick={addSection} title="Přidat sekci">
                    + Sekce
                  </DocActionButton>
                  <DocActionButton onClick={addItem} title="Přidat položku">
                    + Položka
                  </DocActionButton>
                </>
              }
            >
              <div style={{ overflowX: 'auto', margin: '4px 0 10px' }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, width: 28 }}>#</th>
                      <th style={{ ...thStyle, textAlign: 'left' }}>
                        Popis prací / dodávek
                      </th>
                      <th style={{ ...thStyle, width: 46 }}>MJ</th>
                      <th style={{ ...thStyle, width: 64 }}>Množství</th>
                      <th style={{ ...thStyle, width: 90 }}>J. cena (Kč)</th>
                      <th style={{ ...thStyle, width: 100 }}>Celkem (Kč)</th>
                      <th style={{ ...thStyle, width: 60 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {calc.numbered.map(({ row, n, total }, idx) =>
                      row.type === 'section' ? (
                        <tr
                          key={row.id}
                          style={{ background: 'rgba(18,50,90,0.85)' }}
                        >
                          <td
                            style={{
                              ...tdStyle,
                              color: '#fff',
                              fontWeight: 700,
                              textAlign: 'center',
                            }}
                          >
                            §
                          </td>
                          <td
                            colSpan={5}
                            style={{
                              ...tdStyle,
                              padding: '4px 8px',
                              color: '#fff',
                            }}
                          >
                            <input
                              value={row.title}
                              onChange={(e) =>
                                updateRow(row.id, { title: e.target.value })
                              }
                              placeholder="Název sekce"
                              style={sectionInput}
                            />
                          </td>
                          <td style={{ ...tdStyle, padding: '4px 6px' }}>
                            <RowActions
                              onUp={idx > 0 ? () => moveRow(row.id, -1) : undefined}
                              onDown={
                                idx < calc.numbered.length - 1
                                  ? () => moveRow(row.id, 1)
                                  : undefined
                              }
                              onRemove={() => removeRow(row.id)}
                              dark
                            />
                          </td>
                        </tr>
                      ) : (
                        <tr key={row.id}>
                          <td style={{ ...tdStyle, color: 'var(--sd-doc-dim)' }}>{n}</td>
                          <td style={tdStyle}>
                            <input
                              value={row.title}
                              onChange={(e) =>
                                updateRow(row.id, { title: e.target.value })
                              }
                              placeholder="Popis položky"
                              style={cellInput}
                            />
                          </td>
                          <td style={{ ...tdStyle, padding: '2px 4px' }}>
                            <input
                              value={row.unit ?? ''}
                              onChange={(e) =>
                                updateRow(row.id, { unit: e.target.value })
                              }
                              placeholder="m²"
                              style={{ ...cellInput, textAlign: 'center' }}
                            />
                          </td>
                          <td style={{ ...tdStyle, padding: '2px 4px' }}>
                            <input
                              value={row.qty ?? ''}
                              onChange={(e) =>
                                updateRow(row.id, { qty: e.target.value })
                              }
                              placeholder="0"
                              style={{
                                ...cellInput,
                                textAlign: 'right',
                                fontFamily: 'var(--sd-font-mono)',
                              }}
                            />
                          </td>
                          <td style={{ ...tdStyle, padding: '2px 4px' }}>
                            <input
                              value={row.price ?? ''}
                              onChange={(e) =>
                                updateRow(row.id, { price: e.target.value })
                              }
                              placeholder="0"
                              style={{
                                ...cellInput,
                                textAlign: 'right',
                                fontFamily: 'var(--sd-font-mono)',
                              }}
                            />
                          </td>
                          <td
                            style={{
                              ...tdStyle,
                              textAlign: 'right',
                              fontFamily: 'var(--sd-font-mono)',
                              fontWeight: 600,
                            }}
                          >
                            {fmtCz(total)}
                          </td>
                          <td style={{ ...tdStyle, padding: '2px 4px' }}>
                            <RowActions
                              onUp={idx > 0 ? () => moveRow(row.id, -1) : undefined}
                              onDown={
                                idx < calc.numbered.length - 1
                                  ? () => moveRow(row.id, 1)
                                  : undefined
                              }
                              onRemove={() => removeRow(row.id)}
                            />
                          </td>
                        </tr>
                      ),
                    )}
                    {rows.length === 0 && (
                      <tr>
                        <td
                          colSpan={7}
                          style={{
                            ...tdStyle,
                            textAlign: 'center',
                            color: 'var(--sd-doc-dim)',
                            fontStyle: 'italic',
                            padding: 16,
                          }}
                        >
                          Žádné položky. Přidej sekci nebo položku tlačítkem v hlavičce sekce.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 12,
                  marginTop: 10,
                }}
              >
                <DocDphSelect value={dphPct} onChange={setDphPct} />
                <table
                  style={{
                    width: 320,
                    borderCollapse: 'collapse',
                    fontSize: 12,
                    color: 'var(--sd-doc-ink)',
                  }}
                >
                  <tbody>
                    <tr>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>Celkem bez DPH</td>
                      <td style={{ ...tdRight, fontWeight: 600 }}>{fmtCz(calc.total)}</td>
                    </tr>
                    <tr>
                      <td style={tdStyle}>DPH {dphPct} %</td>
                      <td style={tdRight}>{fmtCz(calc.dph)}</td>
                    </tr>
                    <tr style={{ background: 'var(--sd-yellow-soft)' }}>
                      <td style={{ ...tdStyle, fontWeight: 800 }}>Celkem s DPH</td>
                      <td style={{ ...tdRight, fontWeight: 800 }}>
                        {fmtCz(calc.grandTotal)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </DocBlock>

            <DocBlock
              idx="06"
              title="Podpisy"
              actions={
                <DocActionButton onClick={addExtra} title="Přidat dalšího účastníka">
                  + Účastník
                </DocActionButton>
              }
            >
              <DocRow
                k="Zhotovitel — jméno"
                value={podpisy.zhotovitel.jmeno}
                onChange={(v) =>
                  setPodpisy((p) => ({ ...p, zhotovitel: { ...p.zhotovitel, jmeno: v } }))
                }
              />
              <DocRow
                k="Zhotovitel — funkce"
                value={podpisy.zhotovitel.funkce}
                onChange={(v) =>
                  setPodpisy((p) => ({ ...p, zhotovitel: { ...p.zhotovitel, funkce: v } }))
                }
                placeholder="např. stavbyvedoucí"
              />
              <DocRow
                k="Objednatel — jméno"
                value={podpisy.objednatel.jmeno}
                onChange={(v) =>
                  setPodpisy((p) => ({ ...p, objednatel: { ...p.objednatel, jmeno: v } }))
                }
              />
              <DocRow
                k="Objednatel — funkce"
                value={podpisy.objednatel.funkce}
                onChange={(v) =>
                  setPodpisy((p) => ({ ...p, objednatel: { ...p.objednatel, funkce: v } }))
                }
                placeholder="např. investor"
              />
              {podpisy.extra.map((e, i) => (
                <Fragment key={e.id}>
                  <DocRow
                    k={`Účastník ${i + 1} — strana`}
                    value={e.strana}
                    onChange={(v) => updateExtra(e.id, { strana: v })}
                    actions={
                      <DocActionButton onClick={() => removeExtra(e.id)} title="Odebrat účastníka">
                        ✕
                      </DocActionButton>
                    }
                  />
                  <DocRow
                    k="Jméno"
                    value={e.jmeno}
                    onChange={(v) => updateExtra(e.id, { jmeno: v })}
                  />
                  <DocRow
                    k="Funkce"
                    value={e.funkce}
                    onChange={(v) => updateExtra(e.id, { funkce: v })}
                  />
                </Fragment>
              ))}
            </DocBlock>

            <DocSignatures
              items={[
                {
                  label: 'Zhotovitel',
                  name: podpisy.zhotovitel.jmeno || '—',
                  role: podpisy.zhotovitel.funkce || '',
                  image: podpisy.zhotovitel.image,
                  onSign: (v) =>
                    setPodpisy((p) => ({ ...p, zhotovitel: { ...p.zhotovitel, image: v } })),
                },
                {
                  label: 'Objednatel',
                  name: podpisy.objednatel.jmeno || '—',
                  role: podpisy.objednatel.funkce || '',
                  image: podpisy.objednatel.image,
                  onSign: (v) =>
                    setPodpisy((p) => ({ ...p, objednatel: { ...p.objednatel, image: v } })),
                },
                ...podpisy.extra.map((e) => ({
                  label: e.strana || 'Účastník',
                  name: e.jmeno || '—',
                  role: e.funkce,
                  image: e.image,
                  onSign: (v: string) => updateExtra(e.id, { image: v }),
                })),
              ]}
            />
          </DocPage>
        </div>
      </DocLayout>

      <SendEmailModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        buildHtml={() => buildDocHtml(previewRef.current)}
        docType="soupis"
        docTitle={docTitle}
        defaultTo={obj.email}
        defaultCc={zhot.email}
        defaultSubject={[docTitle, stavba.nazev].filter(Boolean).join(' — ')}
        meta={`Stavba: ${stavba.nazev || '—'}${
          activeStavba?.nazev ? ' · ' + activeStavba.nazev : ''
        }`}
      />
    </>
  );
}

function RowActions({
  onUp,
  onDown,
  onRemove,
  dark = false,
}: {
  onUp?: () => void;
  onDown?: () => void;
  onRemove: () => void;
  dark?: boolean;
}) {
  const baseColor = dark ? 'rgba(255,255,255,0.85)' : 'var(--sd-doc-dim)';
  const removeColor = dark ? '#ffd1cf' : 'var(--sd-danger)';
  const btn: CSSProperties = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: baseColor,
    fontSize: 13,
    padding: '2px 4px',
    lineHeight: 1,
  };
  return (
    <div style={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
      <button type="button" style={btn} disabled={!onUp} onClick={onUp} title="Nahoru">
        ▲
      </button>
      <button type="button" style={btn} disabled={!onDown} onClick={onDown} title="Dolů">
        ▼
      </button>
      <button
        type="button"
        style={{ ...btn, color: removeColor }}
        onClick={onRemove}
        title="Odebrat"
      >
        ✕
      </button>
    </div>
  );
}

const tableStyle: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 11.5,
  color: 'var(--sd-doc-ink)',
};
const thStyle: CSSProperties = {
  padding: '6px 8px',
  border: '1px solid #d0d5de',
  background: 'rgba(18,50,90,0.06)',
  fontWeight: 700,
  fontSize: 10.5,
  textAlign: 'center',
  color: 'var(--sd-doc-ink)',
  lineHeight: 1.3,
};
const tdStyle: CSSProperties = {
  padding: '5px 8px',
  border: '1px solid #d0d5de',
  fontSize: 11.5,
};
const tdRight: CSSProperties = {
  ...tdStyle,
  textAlign: 'right',
  fontFamily: 'var(--sd-font-mono)',
};
const cellInput: CSSProperties = {
  width: '100%',
  background: 'transparent',
  border: 0,
  borderBottom: '1px dashed #d0d5de',
  outline: 0,
  padding: '2px 0',
  fontSize: 11.5,
  color: 'var(--sd-doc-ink)',
  fontFamily: 'inherit',
};
const sectionInput: CSSProperties = {
  width: '100%',
  background: 'transparent',
  border: 0,
  outline: 0,
  padding: '2px 0',
  fontWeight: 700,
  fontSize: 12,
  color: '#fff',
  letterSpacing: '0.02em',
  fontFamily: 'inherit',
};
